/** Pure geo helpers for in-app nav (no Geolocation API in core). */

export const DEFAULT_OFF_ROUTE_METERS = 75;
export const DEFAULT_ROUTE_IDLE_ALERT_MIN = 10;

export type LatLng = { lat: number; lng: number };

export function haversineMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Distance from point to segment AB (meters). */
export function distancePointToSegmentMeters(
  point: LatLng,
  a: LatLng,
  b: LatLng,
): number {
  const ax = a.lng;
  const ay = a.lat;
  const bx = b.lng;
  const by = b.lat;
  const px = point.lng;
  const py = point.lat;
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) {
    return haversineMeters(point, a);
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)));
  const closest = { lat: ay + t * dy, lng: ax + t * dx };
  return haversineMeters(point, closest);
}

/** Min distance from point to polyline (LonLat pairs as [lng, lat]). */
export function distanceToPolylineMeters(
  point: LatLng,
  line: Array<[number, number]>,
): number {
  if (!line.length) return Infinity;
  if (line.length === 1) {
    return haversineMeters(point, { lng: line[0][0], lat: line[0][1] });
  }
  let min = Infinity;
  for (let i = 1; i < line.length; i += 1) {
    const a = { lng: line[i - 1][0], lat: line[i - 1][1] };
    const b = { lng: line[i][0], lat: line[i][1] };
    min = Math.min(min, distancePointToSegmentMeters(point, a, b));
  }
  return min;
}

export function isOffRoute(
  point: LatLng,
  line: Array<[number, number]>,
  thresholdM = DEFAULT_OFF_ROUTE_METERS,
): boolean {
  return distanceToPolylineMeters(point, line) > thresholdM;
}

/**
 * Idle when the runner has not advanced along the route for `idleAlertMin` minutes.
 * `progressMeters` should increase as they move toward remaining stops.
 */
export function shouldRaiseIdleAlert(input: {
  lastProgressAtMs: number | null;
  nowMs: number;
  idleAlertMin?: number;
  /** True when GPS reports meaningful advance since last progress stamp. */
  hasAdvanced?: boolean;
}): boolean {
  const idleMin = Math.max(1, Number(input.idleAlertMin ?? DEFAULT_ROUTE_IDLE_ALERT_MIN));
  if (input.hasAdvanced) return false;
  if (input.lastProgressAtMs == null) return false;
  const elapsed = input.nowMs - input.lastProgressAtMs;
  return elapsed >= idleMin * 60_000;
}

export function clampRouteIdleAlertMin(raw: unknown, fallback = DEFAULT_ROUTE_IDLE_ALERT_MIN): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.min(120, Math.round(n)));
}
